import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const QUERY_KEY = ["/api/my/saved-item-ids"];

export function useSavedItems() {
  const queryClient = useQueryClient();

  const { data } = useQuery<{ itemIds: string[] }>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch("/api/my/saved-item-ids", {
        credentials: "include",
      });
      if (res.status === 401) return { itemIds: [] };
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 30_000,
  });

  const savedSet = new Set(data?.itemIds ?? []);

  const toggleSave = useCallback(
    async (itemId: string) => {
      const wasSaved = savedSet.has(itemId);

      queryClient.setQueryData<{ itemIds: string[] }>(QUERY_KEY, (old) => {
        const ids = new Set(old?.itemIds ?? []);
        if (wasSaved) ids.delete(itemId);
        else ids.add(itemId);
        return { itemIds: [...ids] };
      });

      try {
        await fetch(`/api/items/${itemId}/save`, {
          method: wasSaved ? "DELETE" : "POST",
          credentials: "include",
        });
      } catch {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      }
    },
    [savedSet, queryClient],
  );

  return { savedSet, toggleSave };
}
